import type { Note } from '../transport/api'

/**
 * The text a dispatched note puts in the composer.
 *
 * A note's attachments reach the agent as `<note-file:PATH>` lines, one per file.
 * The tag rather than a bare path, which is what this first shipped as: the system
 * already speaks `<agent-msg:HASH>`, `<ping-agent:HASH:label>` and
 * `<project-msg:ID>`, so a bare path is the thing outside the vocabulary, not the tag.
 * It also has edges — a filename with a space in it disappears into the surrounding
 * prose without them. avengents-server documents the tag in ~/.agent-api/README.md,
 * which is the half of this convention that makes an agent able to act on it.
 *
 * The path is absolute and only means anything on THIS edge, so it is withheld from
 * an agent on another host — see below.
 */
export function noteDispatchText(
  note: Pick<Note, 'text' | 'attachments'>,
  /** Host of the agent this is aimed at. null = this machine. */
  host: string | null,
): { text: string; withheld: number } {
  const files = (note.attachments ?? []).filter((a) => !!a.path)
  /*
   * A remote agent gets the words and not the paths.
   *
   * The file is on this edge; that path does not exist on yy-mbp-home. Sending it
   * anyway costs nothing to produce and produces exactly the failure this whole
   * feature keeps circling: the dispatch looks like it worked, the agent reports it
   * cannot find a file, and the reason is invisible from both ends. v1 of the server
   * side does not copy files between hosts, and it says so rather than pretending.
   */
  if (host !== null) {
    return { text: note.text, withheld: files.length }
  }
  if (!files.length) return { text: note.text, withheld: 0 }
  const tags = files.map((a) => `<note-file:${a.path}>`).join('\n')
  // A blank line, so the tags read as attachments rather than as part of the sentence
  // the note ends on.
  return { text: note.text ? `${note.text}\n\n${tags}` : tags, withheld: 0 }
}
